import { useSelected } from '../../providers/selectedContext';
import { useTheme } from '../../providers/theme';
import { getColors } from '../../components/utils/getColors';
import '../../components/dashboard/components/tables.css';
import { ConfigProvider, Radio, RadioChangeEvent, Table, TableColumnsType } from 'antd';
import { Accordion } from '../accordion';
import { useMemo, useState } from 'react';

interface LiaoDataType {
  sw: number;
  pc: number;
  correctedsw: number;
  correctedpc: number;
  SwD: number;
  PcD: number;
  x: number;
  y: number;
}

interface LambdaDataType {
  ln_swn: number | null;
  ln_pc: number | null;
  ln_swn_squared: number | null;
  ln_pc_squared: number | null;
  PcD: number | null;
  SwD: number | null;
  lambda_curve: number | null;
}

const liaoColumns: TableColumnsType<LiaoDataType> = [
  { title: 'Sw fraction', dataIndex: 'sw', key: 'sw' },
  { title: 'Laboratory Pressure Psia', dataIndex: 'pc', key: 'pc' },
  { title: 'Sw Corrected fraction', dataIndex: 'correctedsw', key: 'correctedsw' },
  { title: 'Pres (Psia)', dataIndex: 'correctedpc', key: 'correctedpc' },
  { title: 'Swd', dataIndex: 'SwD', key: 'SwD' },
  { title: 'Pcd', dataIndex: 'PcD', key: 'PcD' },
  { title: 'X', dataIndex: 'x', key: 'x' },
  { title: 'Y', dataIndex: 'y', key: 'y' },
];

const lambdaColumns: TableColumnsType<LambdaDataType> = [
  { title: 'Ln (Swn)', dataIndex: 'ln_swn', key: 'ln_swn' },
  { title: 'Ln (Pc)', dataIndex: 'ln_pc', key: 'ln_pc' },
  { title: 'Ln (Swn)²', dataIndex: 'ln_swn_squared', key: 'ln_swn_squared' },
  { title: 'Ln (Pc)²', dataIndex: 'ln_pc_squared', key: 'ln_pc_squared' },
  { title: 'Swd', dataIndex: 'SwD', key: 'SwD' },
  { title: 'Pcd', dataIndex: 'PcD', key: 'PcD' },
  { title: 'Local exponent m', dataIndex: 'lambda_curve', key: 'lambda_curve' },
];

const Curves = () => {
  const { selectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const [showLambda, setShowLambda] = useState(false);

  const hasLiaoData = !!(selectedInfo?.sample?.analysis_curves?.liao?.x?.length);
  const hasLambdaData = !!(selectedInfo?.sample?.analysis_curves?.lambda_curves?.ln_pc?.length);

  const liaoData: LiaoDataType[] = useMemo(() => {
    const sampleCurves = selectedInfo.sample?.input_sample_curves;
    const liaoSampleData = selectedInfo.sample?.analysis_curves.liao;
    if (!liaoSampleData?.x) return [];
    return liaoSampleData.x.map((_, index) => ({
      sw: sampleCurves?.pc_model?.sw?.[index] || 0,
      pc: sampleCurves?.pc_model?.pc?.[index] || 0,
      correctedsw: liaoSampleData.CorrectedSw?.[index] || 0,
      correctedpc: liaoSampleData.Pres?.[index] || 0,
      SwD: liaoSampleData.SwD?.[index] || 0,
      PcD: liaoSampleData.PcD?.[index] || 0,
      x: liaoSampleData.x?.[index] || 0,
      y: liaoSampleData.y?.[index] || 0,
      key: `liao-sample-data-${index}`,
    }));
  }, [selectedInfo.sample]);

  const lambdaData: LambdaDataType[] = useMemo(() => {
    const lambdaSampleData = selectedInfo.sample?.analysis_curves.lambda_curves;
    if (!lambdaSampleData?.ln_pc) return [];
    return lambdaSampleData.ln_pc.map((_, index) => ({
      ln_swn: lambdaSampleData.ln_swn?.[index] ?? null,
      ln_pc: lambdaSampleData.ln_pc?.[index] ?? null,
      ln_swn_squared: lambdaSampleData.ln_swn_squared?.[index] ?? null,
      ln_pc_squared: lambdaSampleData.ln_pc_squared?.[index] ?? null,
      PcD: lambdaSampleData.PcD?.[index] ?? null,
      SwD: lambdaSampleData.SwD?.[index] ?? null,
      lambda_curve: lambdaSampleData.lambda_curve?.[index] ?? null,
      key: `lambda-sample-data-${index}`,
    }));
  }, [selectedInfo.sample?.analysis_curves.lambda_curves]);

  const handleDataTypeChange = (event: RadioChangeEvent) => setShowLambda(event.target.value);

  if (!hasLiaoData && !hasLambdaData) {
    return (
      <Accordion defaultOpen="curves">
        <Accordion.Item id="curves" header="Curves">
          <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.7, color: colors.text }}>
            No analysis data available. Go to <strong>Sample Measurements</strong> to add Sw and Pc values and click Calculate.
          </p>
        </Accordion.Item>
      </Accordion>
    );
  }

  return (
    <Accordion defaultOpen="curves">
      <Accordion.Item id="curves" header="Curves">
        <Radio.Group
          onChange={handleDataTypeChange}
          options={[
            { label: 'Liao Analysis', value: false },
            { label: 'Brooks-Corey Analysis', value: true },
          ]}
          defaultValue={false}
          style={{ width: '100%', backgroundColor: 'transparent !important', paddingBottom: '1rem' }}
          block
          optionType="button"
          buttonStyle="solid"
          className={isDay ? 'day-radio' : 'night-radio'}
        />
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: isDay ? '#fafafa' : '#090909',
                borderColor: isDay ? '#f0f0f0' : '#434343',
                rowHoverBg: isDay ? '#f5f5f5' : '#1f1f1f',
                stickyScrollBarBg: isDay ? 'rgba(0, 0, 0, 0.25)' : '#434343',
              },
            },
            token: {
              colorText: colors.text,
              colorBorder: colors.border,
              colorBgBase: colors.bg,
            },
          }}
        >
          {!showLambda ? (
            <Table<LiaoDataType> columns={liaoColumns} dataSource={liaoData} pagination={false} scroll={{ x: true }} />
          ) : (
            <Table<LambdaDataType> columns={lambdaColumns} dataSource={lambdaData} pagination={false} scroll={{ x: true }} />
          )}
        </ConfigProvider>
      </Accordion.Item>
    </Accordion>
  );
};

export default Curves;
